(ns frontend.view.grid.companies-grid.view
  (:require
   [common.attribute :as attribute]
   [common.fields.table-column-widths :as widths]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.sync-progress :as sync-progress]
   [common.teams :as teams]
   [frontend.db :as app-db]
   [frontend.local-storage :as ls]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.components :as grid-components]
   [frontend.react.virtualized.scrollable-grid.kanban :as kanban]
   [frontend.react.virtualized.scrollable-grid.view :as scrollable-grid]
   [frontend.react.virtualized.utils :as vu]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.grid-filters.view :as grid-filters]
   [frontend.view.entity :as entity]
   [frontend.view.grid.common.components :as components]
   [frontend.view.grid.common.utils :as grid-utils]
   [frontend.view.grid.companies-grid.db :as db]
   [frontend.view.grid.companies-grid.events :as events]
   [frontend.view.modals.bulk-edit-entity.view :as bulk-edit-modal]
   [frontend.view.named-view.events :as named-view-events]
   [frontend.view.syncing-data :as sync]
   [frontend.ux-overhaul.listing.company.view :as company-listing]
   [potpuri.core :as potpuri]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.editors.entity-creator.view :as creator-editor]))

(defonce select-width 30)

(defn column
  [args]
  (let [{:keys [label attribute field-schema filter-data sorting-disabled? width hover-atom
                recompute-grid-sizes! flex-grow]}
        args
        default-width (-> (widths/get-column-width attribute)
                          :default-width
                          (or widths/default-width))]
    {:label               label
     :data-key            attribute
     :field-schema        field-schema
     :filter-data         filter-data
     :update-filter!      (fn [k filter-data]
                            (grid/set-filter (events/setup-args) k filter-data))
     :sort-disabled?      (boolean sorting-disabled?)
     :min-width           (-> (widths/get-column-width attribute)
                              :min-width
                              (or widths/min-width))
     :max-width           (-> (widths/get-column-width attribute)
                              :max-width
                              (or widths/max-width))
     :default-width       default-width
     :width               (or width default-width)
     :set-width           width
     :begin-width-update! (r/partial grid/begin-width-update! hover-atom attribute)
     :update-width!
     (r/partial grid/update-width!
                (events/setup-args)
                recompute-grid-sizes!
                hover-atom
                attribute)
     :end-width-update!   (r/partial grid/end-width-update! hover-atom attribute)
     :get-styling         (r/partial grid/get-styling hover-atom attribute)
     :flex-grow           flex-grow}))

(defn toolbar-actions
  [{:keys [view-mode]}]
  (let [all-teams (->> @session/all-teams
                       (remove :common/disabled?))
        account-teams @(r/track teams/resolve-account-teams @session/account all-teams)]
    [{:toolbar-button?  true
      :dropdown-action? true
      :ord              2
      :hidden?          (r/partial = view-mode :board-mode)
      #_(fn []
          (= view-mode :board-mode))
      :primary-button?  false
      :add-ellipsis?    true
      :on-select        (fn [_e]
                          (grid/open-select-columns-curtain (events/setup-args)))
      :label            (loc/tr :action/select-columns)}
     (grid-utils/export-button {:account-teams  account-teams
                                :entity-type    :entity.type/company
                                :button-ord     3
                                :grid-state     db/companies
                                :column-keys-fn events/resolve-column-keys})
     (grid-utils/export-with-persons-button {:account-teams  account-teams
                                             :entity-type    :entity.type/company
                                             :button-ord     4
                                             :grid-state     db/companies
                                             :column-keys-fn events/resolve-column-keys})
     (when-not (session/is-read-only-user?)
       {:toolbar-button?  true
        :dropdown-action? true
        :ord              1
        :icon             :fa-plus
        :primary-button?  true
        :on-select        (fn [_e]
                            (entity/open-creator! :entity.type/company {}))
        :label            (loc/tr :company-add-button)})

     {:divider? true
      :dropdown-action? true
      :ord 14}

     (grid-utils/creator-config-item
      {:ord 15
       :entity-type :entity.type/company})

     (when (= view-mode :board-mode)
       {:toolbar-button?  false
        :dropdown-action? true
        :ord              16
        :add-ellipsis?    true
        :label            (loc/tr :card-settings)
        :on-select        (fn [_e]
                            (events/open-card-settings!))})

     {:divider? true
      :dropdown-action? true
      :ord 17}

     (grid-utils/preferred-landing-page-toggle {:view-id :companies-grid})]))

(defn toolbar-buttons-renderer
  []
  (let [setup-args (events/setup-args)
        modal-atom (:modal-state setup-args)
        open-bulk-edit! #(swap! modal-atom assoc :bulk-edit-modal true)
        selected (grid/get-selected-items setup-args)]
    [:div
     {:class "flex gap-2"}
     (if (session/is-read-only-user?)
       [:span (loc/tr :no-grid-actions-for-read-only-user)]
       [:<>
        [components/toolbar-delete-button
         {:max-delete-count   10000
          :selected-items     selected
          :on-delete-clicked! events/delete-selected-clicked}]
        [components/toolbar-bulk-edit-button
         {:max-bulk-edit-count   10000
          :selected-items        selected
          :on-bulk-edit-clicked! open-bulk-edit!}]
        (when (session/entity-merge?)
          [components/toolbar-merge-button
           {:selected-items selected
            :entity-type    :entity.type/company}])])]))

(defn on-row-click!
  [sorted-companies row]
  (let [data (when (< row (count sorted-companies))
               (nth sorted-companies row))]
    (routing/open-entity (:db/id data))))

(def can-filter? (constantly true))

(defn resizing-column
  [hover-atom]
  (some-> @hover-atom
          (get :resize-start-width)
          (first)
          (key)))

(defn all-selection!
  [setup-args]
  (let [selected (:selected @db/companies)]
    (if (or (= :all selected) (not-empty selected))
      (grid/clear-selection! setup-args)
      (swap! db/companies assoc :selected :all))))

(defn on-row-select!
  [sorted-companies setup-args selected index checked]
  (let [data (when (< index (count sorted-companies))
               (nth sorted-companies index))
        company-id (:db/id data)]
    (if (= selected :all)
      (grid/select-all-but setup-args company-id)
      (if (boolean checked)
        (swap! db/companies assoc-in [:selected company-id] true)
        (swap! db/companies potpuri/dissoc-in [:selected company-id])))))

(defn create-props-atom
  [selected sorted-companies filters grid-store
   recompute-grid-sizes! hover-atom modal-atom]
  (let [setup-args @(r/track events/setup-args)
        tenant-schema @session/tenant-schema
        columns (vec (keep (fn [k]
                             (let [field-schema (registry/get-field-schema tenant-schema k)
                                   label (attribute/attribute-label tenant-schema k)]
                               (column {:label                 label
                                        :attribute             k
                                        :field-schema          field-schema
                                        :filter-data           (get filters k)
                                        :sorting-disabled?     false
                                        :width                 (grid/get-saved-width setup-args k)
                                        :hover-atom            hover-atom
                                        :recompute-grid-sizes! recompute-grid-sizes!
                                        :flex-grow             1})))
                           (events/resolve-column-keys)))
        editable-grid? (and (session/has-feature-access? :feature/editable-grid)
                            @session/editable-grid?)]
    {:grid-store              grid-store
     :setup-args              (events/setup-args)
     :grid-entity-ids         (->> sorted-companies
                                   (mapv :db/id)
                                   (into #{}))
     :selected                selected
     :link-to-entity-field    :asset/name
     :header-height           50
     :select-width            select-width
     :editable-grid?          editable-grid?
     :min-columns-width       30
     :height                  (- (or (:height @app-db/window) 400)
                                 75)                                                                ;; TODO: lazy - fixed 120px for button row in this view
     :avail-height            (- (or (:height @app-db/window) 400)
                                 75)                                                                ;; TODO: lazy - fixed 120px for button row in this view
     :useDynamicRowHeight     false                                                                 ;; TODO: kebab-case
     :row-height              40                                                                    ;(fn [index] 40)
     :row-count               (if (sequential? sorted-companies) (count sorted-companies) 0)
     :scrollToIndex           0
     :columns                 columns
     :overscan-count          1
     :no-rows-renderer        (r/partial grid/default-no-rows-render
                                         @db/companies
                                         :entity.type/company)
     :on-row-click!           (r/partial on-row-click! sorted-companies)
     :on-cell-click           (fn [row col]
                                (grid/handle-cell-click! {:columns              columns
                                                          :col                  col
                                                          :row                  row
                                                          :sorted-grid-entities sorted-companies
                                                          :state                hover-atom}))
     :is-sorted-by?           (partial grid/is-sorted-by db/view-settings)
     :is-secondary-sorted-by? (partial grid/is-secondary-sorted-by db/view-settings)
     :sort-by!                (fn [k dir]
                                (grid/set-sort-by (events/setup-args) k dir))
     :reorder-columns!        (fn [dragged-column after-column]
                                (grid/reorder-columns! setup-args dragged-column after-column)
                                (recompute-grid-sizes!))
     :can-filter?             can-filter?
     :resizing-column         @(r/track resizing-column hover-atom)
     :recompute-grid-sizes!   recompute-grid-sizes!
     :all-selection!          (r/partial all-selection! setup-args)
     :select-action-renderer  toolbar-buttons-renderer
     :on-row-select!          (r/partial on-row-select! sorted-companies setup-args selected)
     :deselect-entity!        (fn [entity-id]
                                (if (= selected :all)
                                  (grid/select-all-but (events/setup-args) entity-id)
                                  (swap! db/companies potpuri/dissoc-in [:selected entity-id])))
     :row-data-getter         (r/partial grid/default-row-data-getter sorted-companies selected)

     :new-action-view         {:in-use?         true
                               :open-bulk-edit! #(swap! modal-atom assoc :bulk-edit-modal true)
                               :bulk-delete!    events/delete-selected-clicked
                               :modal-atom      modal-atom}}))

(defn edit-filter!
  [k filter-data]
  (let [tenant-schema @session/tenant-schema]
    (grid/open-filter-curtain
     {:field-key      k
      :field-schema   (registry/get-field-schema tenant-schema k)
      :options        (->> (events/resolve-column-key-universe)
                           (map (juxt identity (partial registry/get-field-schema tenant-schema))))
      :current-filter filter-data
      :update-fn!     (fn [k filter-data]
                        (grid/set-filter (events/setup-args) k filter-data))}
     nil)))

(defn load-view!
  [named-view]
  (events/load-view! named-view)
  (grid/clear-selection! {:state db/companies}))

(defn toggle-minimize!
  [recompute-grid-sizes!]
  (events/toggle-quick-filters!)
  (.setTimeout js/window recompute-grid-sizes! 1000))

(defn render
  [grid-store _args]
  (let [hover-atom (r/atom {:hover {:col nil :row nil}})
        entity-type :entity.type/company
        recompute-grid-sizes! (fn []
                                (js/setTimeout
                                 (partial vu/recompute-grid-sizes-with-state! grid-store)
                                 0))]
    (fn [{:keys [view-mode]}]
      (let [selected (:selected @db/companies)
            tenant-schema @session/tenant-schema
            filters {}
            #_@db/company-filters                                                                   ;;TODO later
            sorted-companies @db/sorted-grid-entities
            setup-args @(r/track events/setup-args)
            modal-atom (:modal-state setup-args)
            grid-atom (r/track create-props-atom
                               selected
                               sorted-companies
                               filters
                               grid-store
                               recompute-grid-sizes!
                               hover-atom
                               modal-atom)
            show-quick-filters? @db/show-quick-filters?
            card-settings? @db/card-settings?

            show-bulk-edit-modal? (:bulk-edit-modal @modal-atom)

            filters-props {:minimized?            (not show-quick-filters?)
                           :hide-maximize?        true
                           :universal-search?     true
                           :search-placeholder    (loc/tr :search-for-a-company)
                           :setup-args            setup-args
                           :set-universal-search! (r/partial grid/set-universal-search! setup-args)
                           :set-name-search!      (r/partial grid/set-name-search! setup-args)
                           :hide-named-views?     true
                           ;;:named-views           @(r/track events/named-views)
                           :current-view          @db/view-settings
                           :columns               @(r/track events/resolve-column-keys)
                           :all-columns           @(r/track events/resolve-column-key-universe)
                           :get-column-label      (partial attribute/attribute-label tenant-schema)
                           :set-filter!           (r/partial grid/set-filter setup-args)
                           :edit-filter!          edit-filter!
                           :hide-qf-attributes    [:asset/name :company/pfc-responsible]
                           :load-view!            load-view!
                           :save-view!            events/save-current-view!
                           :delete-view!          named-view-events/delete-view!
                           :toggle-minimize!      (r/partial toggle-minimize! recompute-grid-sizes!)}

            hiccup (if card-settings?
                     [kanban/card-settings-view setup-args]
                     [:<>
                      [grid-filters/view
                       filters-props]
                      [creator-editor/modal-view]
                      [:div.grid-view.px-4
                       [:div
                        {:class (fu/cs
                                 "flex flex-row flex-auto"
                                 "grid-view-content"
                                 (when-not show-quick-filters? "named-views-minimized")
                                 (when (= view-mode :board-mode) "board-view"))}

                        (when show-bulk-edit-modal?
                          [bulk-edit-modal/view
                           {:setup-args   setup-args
                            :entity-type  entity-type
                            :close-modal! #(swap! modal-atom dissoc :bulk-edit-modal)}])

                        (if (= view-mode :board-mode)
                          [kanban/view
                           {:setup-args   setup-args
                            :card-actions [(kanban/open-entity-action
                                            {:entity-type :entity.type/company})]}]
                          [scrollable-grid/view
                           {:props-atom grid-atom
                            :hover-atom hover-atom}])]]])]
        hiccup))))

(defn set-grouped-by!
  [k]
  (swap! db/view-settings assoc :grouped-by k)
  (ls/update-in (events/ls-path)
                assoc
                :grouped-by
                k))

(defn load-named-view!
  [id]
  (events/load-view! (events/named-view-by-id id))
  (grid/clear-selection! {:state db/companies}))

(defn toolbar
  [{:keys [view-mode]}]
  (let [board-mode? (= view-mode :board-mode)
        card-mode (or @db/card-mode
                      :short-card)
        setup-args @(r/track events/setup-args)
        column-key-universe @(r/track events/resolve-column-key-universe)
        has-empty-groups? (when board-mode?
                            (kanban/has-empty-groups? @(r/track events/setup-args)))]
    [:div
     {:class "px-4 py-1 flex items-center justify-between
              bg-base-light
              border-b border-solid border-base-light-border-paler"}
     [:div
      {:class "w-full flex justify-between items-center"}
      [:div
       [:div
        {:class "flex items-center gap-4"}
        [:div
         {:class "flex gap-2 items-center"}
         [:label
          {:class "font-semibold"}
          (loc/tr :companies-grid)]

         [:span
          {:class "h-5 px-2 flex items-center justify-center text-xs font-semibold text-base-light-text bg-complement-regular rounded-full"}
          (count @db/sorted-kanban-entities)]]

        [grid-components/named-view-selector
         {:named-views               @(r/track events/named-views)
          :current-view              @db/view-settings
          :load-view!                load-named-view!
          :delete-view!              named-view-events/start-delete-view!
          :accounts-with-view-access (session/active-accounts nil)
          :save-current-view!        events/save-current-view!}]

        (when board-mode?
          [grid-components/sort-selector
           {:view-settings   @db/view-settings
            :entity-type     :entity.type/company
            :sortable-fields column-key-universe
            :set-sort-by!    (r/partial grid/set-sort-by setup-args)}])

        (when board-mode?
          [grid-components/grouped-by-selector
           {:view-settings      @db/view-settings
            :entity-type        :entity.type/company
            :default-grouped-by :company/company-owner
            :grouped-by-fields  (->> column-key-universe
                                     (filter grid/groupable-attribute?))
            :set-grouped-by!    set-grouped-by!}])]]
      (when (and (session/has-feature-access? :feature/enrichment-clearbit)
                 (:account/nylas-account-id @session/account)
                 (:account/nylas-initial-sync-started-on @session/account)
                 (not (:account/nylas-initial-sync-completed-on @session/account)))
        [:div.toolbar-middle
         {:style {:flex "1 1 100%"}}
         [:span
          {:style {:background "#F6E7BA"
                   :padding    "8px"}}
          (loc/tr :initial-automatic-company-creation-in-progress)]])
      [:div
       {:class "flex items-center gap-3"}
       ;;(when-not board-mode?
       ;;  [grid-components/editable-grid-toggle])
       (when (and board-mode? (not= card-mode :short-card))
         [button/square-secondary-open
          {:on-click (fn [_] (events/collapse-all!))
           :tooltip  (loc/tr :collapse-all)}
          [:i.fal.fa-compress-alt]])
       (when (and board-mode? (= card-mode :short-card))
         [button/square-secondary-open
          {:tooltip  (loc/tr :expand-all)
           :on-click (fn [_] (events/expand-all!))}
          [:i.fal.fa-expand-alt]])

       [button/square-secondary-open
        {:tooltip  (grid-components/view-mode-tooltip board-mode?)
         :on-click (fn [_]
                     (events/switch-mode! view-mode))}
        (if board-mode?
          [:i.fal.fa-table]
          [:i.fal.fa-signal-3.fa-rotate-180.kanban-icon])]

       (when (and board-mode? has-empty-groups?)
         [grid-utils/hide-empty-kanban-groups-toggle "companies-grid-toolbar" db/kanban])

       [:div.toolbar-dropdown
        [zap-toolbar/dropdown
         (->> @(r/track toolbar-actions {:view-mode view-mode})
              (filter :dropdown-action?)
              (remove (fn [{:keys [hidden?]}]
                        (when (fn? hidden?)
                          (hidden?))))
              (sort-by :ord))
         true]]]]]))

(defn view
  [_args]
  (let [grid-store (atom {})
        close-atom (atom false)
        resizer (fn []
                  (js/setTimeout (fn [_]
                                   (vu/recompute-grid-sizes-with-state! grid-store))
                                 100))]
    (.addEventListener js/window "resize" resizer)
    (r/create-class
     {:component-did-mount    (fn [_]
                                (fu/timeout 10 (partial events/setup close-atom)))
      :component-will-unmount (fn []
                                (reset! close-atom true)
                                (events/cleanup)
                                (.removeEventListener js/window "resize" resizer))
      :reagent-render         (fn [args]
                                (render grid-store args))})))

(defmethod routing/render-view :companies-grid
  []
  (if (session/new-grid-2023?)
    [default-page-layout/view
     [:<>
      [company-listing/page-content]]]
    (let [view-mode (or @db/view-mode
                        (:view-mode (ls/get-in (events/ls-path)))
                        :grid-mode)]
      [default-page-layout/view
       [sync/wait-until-sync-progress sync-progress/COMPANIES
        [:<>
         [components/card-settings-toolbar
          [toolbar {:view-mode view-mode}] db/kanban events/close-card-settings!]
         [view {:view-mode view-mode}]]]])))
